import { Button, Checkbox, Popup, SearchBar, Selector, Skeleton, TextArea, Toast } from "antd-mobile";
import { CheckOutline, LeftOutline } from "antd-mobile-icons";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  FeedbackDishOption,
  getFeedbackDishOptions,
  getQualityRecord,
  getQualityRecords,
  MobileQualityRecord,
  submitQualityFeedback,
  requestTrialQualityReview,
  TrialReviewReason,
} from "../../../api/mobileQuality";
import {
  FeedbackDimension,
  FeedbackErrorType,
  QualityReviewContext,
  QualityFeedbackPayload,
  StandardIssueType,
  takeNextQualityRecord,
} from "../../../utils/mobileQuality";
import { trialOutcomeLabel } from "../../../utils/qualityPresentation";
import styles from "./index.module.css";

interface QualityDetailLocationState {
  qualityReviewContext?: QualityReviewContext;
}

const errorLabels: Record<FeedbackErrorType, string> = {
  DISH_RECOGNITION_WRONG: "菜品识别错了",
  SHOULD_BE_QUALIFIED: "这道菜应该达标",
  SHOULD_BE_UNQUALIFIED: "这道菜应该不达标",
  REASON_INACCURATE: "结论对，但原因不对",
  STANDARD_INACCURATE: "标准图或标准不合理",
  IMAGE_UNCLEAR: "图片看不清，无法判断",
  OTHER: "其他问题",
};

const dimensionOptions: { label: string; value: FeedbackDimension }[] = [
  { label: "容器", value: "CONTAINER" },
  { label: "汤汁", value: "BROTH" },
  { label: "核心食材", value: "INGREDIENT" },
  { label: "摆盘", value: "PLATING" },
  { label: "色泽", value: "COLOR" },
  { label: "份量", value: "PORTION" },
  { label: "火候", value: "COOKING_STATE" },
  { label: "拍摄角度或灯光", value: "CAPTURE_CONDITION" },
  { label: "其他", value: "OTHER" },
];

const standardIssueOptions: { label: string; value: StandardIssueType }[] = [
  { label: "标准图不是这道菜", value: "WRONG_DISH" },
  { label: "容器标准不合理", value: "CONTAINER" },
  { label: "摆盘标准不合理", value: "PLATING" },
  { label: "汤汁标准不合理", value: "BROTH" },
  { label: "份量标准不合理", value: "PORTION" },
  { label: "标准图已过时", value: "OUTDATED" },
  { label: "其他", value: "OTHER" },
];

const trialReviewReasons: { value: TrialReviewReason; label: string }[] = [
  { value: "LIGHTING", label: "灯光影响" },
  { value: "IMAGE_QUALITY", label: "照片没拍清楚" },
  { value: "STANDARD_MISMATCH", label: "标准不适用" },
  { value: "SCORING", label: "扣分过重" },
  { value: "OTHER", label: "其他" },
];
const directionNames = { EXCESS: "偏多", INSUFFICIENT: "偏少", DARK: "偏深", LIGHT: "偏浅",
  MISSING: "缺失", NONE: "无偏差", OTHER: "其他", UNKNOWN: "无法判断" };

const QualityDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { recordId = "" } = useParams();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [record, setRecord] = useState<MobileQualityRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingNext, setLoadingNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rejectPopup, setRejectPopup] = useState(false);
  const [errorType, setErrorType] = useState<FeedbackErrorType>();
  const [disputedDimensions, setDisputedDimensions] = useState<FeedbackDimension[]>([]);
  const [actualDimensions, setActualDimensions] = useState<FeedbackDimension[]>([]);
  const [standardIssues, setStandardIssues] = useState<StandardIssueType[]>([]);
  const [correctedDishId, setCorrectedDishId] = useState<string>();
  const [dishMissing, setDishMissing] = useState(false);
  const [dishOptions, setDishOptions] = useState<FeedbackDishOption[]>([]);
  const [dishSearch, setDishSearch] = useState("");
  const [dishLoading, setDishLoading] = useState(false);
  const [note, setNote] = useState("");
  const [trialNote, setTrialNote] = useState("");
  const [trialSubmitting, setTrialSubmitting] = useState(false);
  const [trialReviewReason, setTrialReviewReason] = useState<TrialReviewReason>();
  const trialRequestKey = useRef(`${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const reviewContext = (location.state as QualityDetailLocationState | null)?.qualityReviewContext;

  useEffect(() => {
    let active = true;
    trialRequestKey.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setTrialReviewReason(undefined);
    setTrialNote("");
    setLoading(true);
    setRecord(null);
    getQualityRecord(recordId)
      .then((value) => active && setRecord(value))
      .catch(() => active && Toast.show({ content: "品质详情加载失败" }))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [recordId]);

  useEffect(() => {
    if (!rejectPopup || errorType !== "DISH_RECOGNITION_WRONG" || dishOptions.length) return;
    setDishLoading(true);
    getFeedbackDishOptions()
      .then(setDishOptions)
      .catch(() => Toast.show({ content: "菜品列表加载失败，请重试" }))
      .finally(() => setDishLoading(false));
  }, [dishOptions.length, errorType, rejectPopup]);

  const availableErrorTypes = useMemo<FeedbackErrorType[]>(() => {
    const correction: FeedbackErrorType = record?.qualityResult === "QUALIFIED"
      ? "SHOULD_BE_UNQUALIFIED"
      : "SHOULD_BE_QUALIFIED";
    return [
      "DISH_RECOGNITION_WRONG",
      correction,
      ...(record?.qualityResult === "UNQUALIFIED" ? ["REASON_INACCURATE" as const] : []),
      "STANDARD_INACCURATE",
      "IMAGE_UNCLEAR",
      "OTHER",
    ];
  }, [record?.qualityResult]);

  const filteredDishes = useMemo(() => dishOptions.filter((dish) =>
    dish.id !== record?.dishId && dish.name.includes(dishSearch.trim())
  ), [dishOptions, dishSearch, record?.dishId]);

  const resetFeedbackForm = () => {
    setErrorType(undefined);
    setDisputedDimensions([]);
    setActualDimensions([]);
    setStandardIssues([]);
    setCorrectedDishId(undefined);
    setDishMissing(false);
    setDishSearch("");
    setNote("");
  };

  const openRejectPopup = () => {
    resetFeedbackForm();
    setRejectPopup(true);
  };

  const closeRejectPopup = () => {
    if (submitting) return;
    setRejectPopup(false);
  };

  const openNextRecord = (nextRecordId: string, context: QualityReviewContext) => {
    scrollRef.current?.scrollTo({ top: 0 });
    setRejectPopup(false);
    resetFeedbackForm();
    setLoading(true);
    navigate(`/quality/${nextRecordId}`, {
      replace: true,
      state: { qualityReviewContext: context },
    });
  };

  const goToNextRecord = async () => {
    if (!reviewContext || loadingNext || submitting) return;
    const loadedNext = takeNextQualityRecord(reviewContext);
    if (loadedNext) {
      openNextRecord(loadedNext.recordId, loadedNext.context);
      return;
    }
    if (!reviewContext.hasMore) return;

    setLoadingNext(true);
    try {
      const result = await getQualityRecords({
        ...reviewContext.query,
        page: reviewContext.nextPage,
      });
      const pageRecordIds = (result.records || [])
        .map((item) => item.id)
        .filter((id) => id !== recordId);
      const nextContext: QualityReviewContext = {
        ...reviewContext,
        pendingRecordIds: pageRecordIds,
        nextPage: reviewContext.nextPage + 1,
        hasMore: (result.current || reviewContext.nextPage) < (result.pages || 0),
      };
      const pageNext = takeNextQualityRecord(nextContext);
      if (pageNext) {
        openNextRecord(pageNext.recordId, pageNext.context);
      } else {
        navigate(`/quality/${recordId}`, {
          replace: true,
          state: { qualityReviewContext: { ...nextContext, hasMore: false } },
        });
        Toast.show({ content: "已经是当前筛选下最后一张" });
      }
    } catch (error) {
      Toast.show({ content: "下一张加载失败，请重试" });
    } finally {
      setLoadingNext(false);
    }
  };

  const canSubmitDisagreement = Boolean(errorType) && (() => {
    switch (errorType) {
      case "DISH_RECOGNITION_WRONG": return Boolean(correctedDishId) !== dishMissing;
      case "SHOULD_BE_QUALIFIED": return disputedDimensions.length > 0;
      case "SHOULD_BE_UNQUALIFIED": return actualDimensions.length > 0;
      case "REASON_INACCURATE": return disputedDimensions.length > 0 && actualDimensions.length > 0;
      case "STANDARD_INACCURATE": return standardIssues.length > 0;
      case "OTHER": return Boolean(note.trim());
      case "IMAGE_UNCLEAR": return true;
      default: return false;
    }
  })();

  const submitFeedback = async (payload: QualityFeedbackPayload) => {
    if (!recordId || submitting) return;
    setSubmitting(true);
    try {
      const updated = await submitQualityFeedback(recordId, payload);
      setRecord(updated);
      setRejectPopup(false);
      resetFeedbackForm();
      Toast.show({ content: payload.agreed ? "已记录：结果正确" : "已记录具体问题", icon: "success" });
    } catch (error) {
      Toast.show({ content: "反馈未保存，请检查后重试" });
    } finally {
      setSubmitting(false);
    }
  };

  const submitDisagreement = () => {
    if (!errorType || !canSubmitDisagreement) return;
    submitFeedback({
      agreed: false,
      errorType,
      correctedDishId,
      dishMissing: errorType === "DISH_RECOGNITION_WRONG" ? dishMissing : undefined,
      disputedDimensions,
      actualProblemDimensions: actualDimensions,
      standardIssueTypes: standardIssues,
      note,
    });
  };

  if (loading) {
    return <main className={styles.page}><Skeleton.Title animated /><Skeleton.Paragraph lineCount={8} animated /></main>;
  }
  if (!record) {
    return <main className={styles.page}><div className={styles.empty}>记录不存在或无权查看</div></main>;
  }

  const isQualified = record.qualityResult === "QUALIFIED";
  const isReviewRequired = record.qualityResult === "AI_REVIEW_REQUIRED";
  const isCaptureIssue = record.captureStatus === "INVALID_CAPTURE";
  const canFeedback = record.qualityResult === "QUALIFIED" || record.qualityResult === "UNQUALIFIED";
  const feedbackText = record.aiJudgeFeedback === 1
    ? "已确认结果正确"
    : record.aiJudgeFeedback === 0 ? "已反馈结果有误" : "等待人工确认";
  const hasNextRecord = Boolean(
    reviewContext && (reviewContext.pendingRecordIds.length > 0 || reviewContext.hasMore)
  );
  const trial = record.trialResult;
  const trialOutcome = trial ? trialOutcomeLabel(trial) : "";
  const conflictingResults = trial && (
    (trial.outcome === "PASS" && record.qualityResult === "UNQUALIFIED") ||
    (trial.outcome === "FAIL" && record.qualityResult === "QUALIFIED")
  );
  const legacyComparison = <section className={styles.comparison}>
    {record.standardImageUrl
      ? <img src={record.standardImageUrl} alt="原有 AI 标准图" />
      : <div className={styles.noStandard}>暂无标准图</div>}
    <div>
      <small>原有 AI 品质结论</small>
      <h2 className={isQualified ? styles.qualified : isReviewRequired ? styles.review : styles.unqualified}>
        {isQualified ? "达标" : isReviewRequired ? "待复核" : "不达标"}
      </h2>
      {record.temperatureCelsius != null && <span>出餐温度 {record.temperatureCelsius.toFixed(1)}°C</span>}
      <p>{record.aiReason || "AI 未返回具体原因"}</p>
    </div>
  </section>;
  const requestTrialReview = async () => {
    if (!trialReviewReason) {
      Toast.show({ content: "请选择复核原因" });
      return;
    }
    if (trialReviewReason === "OTHER" && !trialNote.trim()) {
      Toast.show({ content: "选择其他时请填写说明" });
      return;
    }
    setTrialSubmitting(true);
    try {
      const updated = await requestTrialQualityReview(recordId, trialReviewReason,
        trialNote.trim(), trialRequestKey.current);
      setRecord(updated);
      Toast.show({ content: "已申请复核，等待负责人处理" });
    } catch {
      Toast.show({ content: "申请尚未成功，请检查网络后重试" });
    } finally {
      setTrialSubmitting(false);
    }
  };

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <button aria-label="返回" onClick={() => navigate(-1)}><LeftOutline /></button>
        <div><span>{record.storeName || "-"}</span><h1>{record.dishName || "品质详情"}</h1></div>
        <button
          type="button"
          className={styles.nextButton}
          disabled={!hasNextRecord || loadingNext || submitting}
          onClick={goToNextRecord}
        >
          {loadingNext ? "加载中" : hasNextRecord ? "下一张" : reviewContext ? "已到最后" : "下一张"}
        </button>
      </header>

      <div ref={scrollRef} className={styles.scroll}>
        <section className={styles.capture}>
          <img src={record.captureImageUrl || ""} alt="菜品实拍" />
          <span>实拍 · {record.capturedAt ? dayjs(record.capturedAt).format("MM-DD HH:mm") : "-"}</span>
        </section>

        {isCaptureIssue ? (
          <section className={`${styles.resultCard} ${styles.captureIssue}`}>
            <small>拍摄状态</small><h2>无效拍摄</h2>
            <p>当前系统仅确认该图片不是有效菜品图片，不细分曝光、模糊或取景问题。</p>
          </section>
        ) : !trial ? legacyComparison : null}

        {!isCaptureIssue && (trial || record.trialRuleStatus === "UNCONFIGURED") && (
          <section className={styles.trialCard}>
            <div className={styles.trialHeader}>
              <div><small>新标准评分 · {trial?.trialLabel || "试运行"}</small><h2 className={trial?.outcome === "PASS" ? styles.qualified : trial?.outcome === "FAIL" ? styles.unqualified : styles.review}>{trial ? trialOutcome : "暂不评分"}</h2></div>
              <strong>{trial?.totalScore == null ? "—" : `${trial.totalScore} /100`}</strong>
            </div>
            {trial ? (
              <>
                {trial.trialStandardImageUrl ? <div className={styles.trialImagePair}>
                  <a href={record.captureImageUrl || "#"} target="_blank" rel="noreferrer"><img src={record.captureImageUrl || ""} alt="本次实拍原图" /><span>本次实拍 · 查看原图</span></a>
                  <a href={trial.trialStandardImageUrl} target="_blank" rel="noreferrer"><img src={trial.trialStandardImageUrl} alt="本次生效标准图" /><span>本次标准 · 查看原图</span></a>
                </div> : <div className={styles.trialReference}>本次使用文字标准试运行</div>}
                <p className={styles.trialOutcome}>
                  可见项目评分
                  <span>合格线 {trial.passAt} 分 · 规则版本 {trial.ruleRevision}</span>
                </p>
                {conflictingResults && <p className={styles.trialConflict}>新评分与原有 AI 结论不同，请以现场人工核对为准。</p>}
                {trial.reason && <p className={styles.trialReason}>{trial.reason}</p>}
                <p className={styles.trialReason}>
                  {trial.outcome === "PASS" ? "可见项目通过；味道、内部熟度和温度仍以现场检查为准。"
                    : trial.outcome === "FAIL" ? "请核对问题项，由负责人确认处理。"
                    : trial.outcome === "PENDING" ? "识别正在进行，请稍后查看。"
                    : trial.outcome === "ERROR" ? "识别未完成，请联系负责人处理。"
                    : "请负责人现场复核；若拍摄条件不可靠，请在固定拍摄点重新拍摄。"}
                </p>
                {!!trial.humanReviews?.length && <div className={styles.humanDecision}>
                  <strong>人工结论：{{ RELEASE: "确认可出餐", ADJUST: "需调整", RETAKE: "请补拍" }[trial.humanReviews[0].decision]}</strong>
                  <span>{trial.humanReviews[0].reviewer_name} · {trial.humanReviews[0].reason}</span>
                  <small>原 AI 分数保留在上方</small>
                </div>}
                {!!trial.retakes?.length && <div className={styles.humanDecision}>
                  <strong>已关联现场补拍</strong>
                  {trial.retakes.map(item => <button key={item.new_record_id} type="button"
                    onClick={() => navigate(`/quality/${item.new_record_id}`)}>查看新记录 #{item.new_record_id} · {item.reason}</button>)}
                </div>}
                <div className={styles.trialItems}>
                  {trial.items?.map(item => (
                    <details className={styles.trialItem} key={item.id}>
                      <summary><strong>{item.name}</strong><span>{item.score == null ? "— / 暂不评分" : `${item.score} / ${item.maxScore} 分`}</span></summary>
                      <p>观察：{item.state === "PASS" ? "正常或允许变化" : item.state === "MINOR" ? "轻微偏差"
                        : item.state === "MAJOR" ? "明显偏差" : item.state === "ABSENT" ? "未见" : "看不清"}
                        {item.direction ? ` · 方向：${directionNames[item.direction]}` : ""}
                        {item.deduction != null ? ` · 扣 ${item.deduction} 分` : ""}</p>
                      <p>可见依据：{item.reason || "暂无可见依据"}</p>
                      {(() => {
                        const standard = trial.rule?.config?.checks?.find(check => check.id === item.id);
                        return standard && <div className={styles.itemStandard}>
                          <p>正常：{standard.standard || "—"}</p>
                          <p>允许差异：{standard.allowed || "—"}</p>
                          {standard.minorDescription && <p>轻微：{standard.minorDescription}</p>}
                          {standard.majorDescription && <p>明显偏差：{standard.majorDescription}</p>}
                          {standard.unknownDescription && <p>看不清：{standard.unknownDescription}</p>}
                        </div>;
                      })()}
                    </details>
                  ))}
                </div>
                {trial.outcome !== "PENDING" && trial.outcome !== "ERROR" && <div className={styles.trialFeedback}>
                  <strong>申请人工复核</strong>
                  {trial.reviewRequest ? <span>{trial.humanReviews?.length ? "已处理 · 查看上方人工结论" : "已提交 · 等待负责人复核"}{trial.reviewRequest.note ? `：${trial.reviewRequest.note}` : ""}</span> : <>
                    <div className={styles.reviewReasons}>{trialReviewReasons.map(item =>
                      <button type="button" key={item.value}
                        aria-pressed={trialReviewReason === item.value}
                        onClick={() => setTrialReviewReason(item.value)}>{item.label}</button>)}</div>
                    <TextArea value={trialNote} onChange={setTrialNote} rows={2} maxLength={500}
                      placeholder={trialReviewReason === "OTHER" ? "请说明具体问题（必填）" : "可补充说明现场情况"} />
                    <Button loading={trialSubmitting} onClick={requestTrialReview}>提交复核申请</Button>
                  </>}
                </div>}
              </>
            ) : <p className={styles.trialReason}>本规格尚未配置标准图与评分规则，暂不评分。</p>}
          </section>
        )}

        {!isCaptureIssue && trial && <details className={styles.legacyDetails}>
          <summary>查看原有 AI 结论 · {isQualified ? "达标" : isReviewRequired ? "待复核" : "不达标"}</summary>
          {legacyComparison}
        </details>}

        {!isCaptureIssue && canFeedback && !trial && (
          <section className={styles.feedback}>
            <div className={styles.feedbackHead}>
              <div><h2>这个判断正确吗？</h2><span>你的选择会用于优化识菜和评分</span></div>
              <em className={record.aiJudgeFeedback == null ? styles.pending : styles.done}>{feedbackText}</em>
            </div>
            {record.feedbackErrorType && record.feedbackErrorType !== "UNSPECIFIED" && (
              <div className={styles.feedbackSummary}>
                <strong>{errorLabels[record.feedbackErrorType]}</strong>
                {record.correctedDishName && <span>正确菜品：{record.correctedDishName}</span>}
              </div>
            )}
            {record.aiJudgeFeedbackNote && <p className={styles.note}>补充：{record.aiJudgeFeedbackNote}</p>}
            <div className={styles.actions}>
              <Button loading={submitting} className={styles.agree} onClick={() => submitFeedback({ agreed: true, note: null })}>
                <CheckOutline /> 结果正确
              </Button>
              <Button disabled={submitting} className={styles.reject} onClick={openRejectPopup}>
                结果有误
              </Button>
            </div>
          </section>
        )}
      </div>

      <Popup visible={rejectPopup} onMaskClick={closeRejectPopup} bodyClassName={styles.popup}>
        <div className={styles.popupHead}>
          <span>人工复核</span>
          <h2>结果哪里有误？</h2>
          <p>先选一个主要问题，我们只追问必要信息。</p>
        </div>

        <div className={styles.errorTypes}>
          {availableErrorTypes.map((type) => (
            <button
              key={type}
              type="button"
              className={errorType === type ? styles.errorTypeActive : ""}
              onClick={() => {
                setErrorType(type);
                setDisputedDimensions([]);
                setActualDimensions([]);
                setStandardIssues([]);
                setCorrectedDishId(undefined);
                setDishMissing(false);
              }}
            >
              <i>{errorType === type ? "✓" : ""}</i>
              <span>{errorLabels[type]}</span>
            </button>
          ))}
        </div>

        {errorType === "DISH_RECOGNITION_WRONG" && (
          <div className={styles.followup}>
            <h3>正确菜品是什么？</h3>
            <SearchBar value={dishSearch} onChange={setDishSearch} placeholder="搜索菜名" />
            <div className={styles.dishList} aria-busy={dishLoading}>
              {dishLoading ? <span>正在加载菜品…</span> : filteredDishes.slice(0, 30).map((dish) => (
                <button
                  type="button"
                  key={dish.id}
                  className={correctedDishId === dish.id ? styles.dishActive : ""}
                  onClick={() => { setCorrectedDishId(dish.id); setDishMissing(false); }}
                >
                  {dish.name}<i>{correctedDishId === dish.id ? "✓" : ""}</i>
                </button>
              ))}
            </div>
            <Checkbox checked={dishMissing} onChange={(checked) => { setDishMissing(checked); if (checked) setCorrectedDishId(undefined); }}>
              正确菜品不在菜品库中
            </Checkbox>
          </div>
        )}

        {(errorType === "SHOULD_BE_QUALIFIED" || errorType === "REASON_INACCURATE") && (
          <div className={styles.followup}>
            <h3>AI 哪些判断不成立？</h3>
            <Selector multiple options={dimensionOptions} value={disputedDimensions} onChange={(value) => setDisputedDimensions(value as FeedbackDimension[])} />
          </div>
        )}

        {(errorType === "SHOULD_BE_UNQUALIFIED" || errorType === "REASON_INACCURATE") && (
          <div className={styles.followup}>
            <h3>实际有哪些问题？</h3>
            <Selector multiple options={dimensionOptions.filter((item) => item.value !== "CAPTURE_CONDITION")} value={actualDimensions} onChange={(value) => setActualDimensions(value as FeedbackDimension[])} />
          </div>
        )}

        {errorType === "STANDARD_INACCURATE" && (
          <div className={styles.followup}>
            <h3>标准哪里不合理？</h3>
            <Selector multiple options={standardIssueOptions} value={standardIssues} onChange={(value) => setStandardIssues(value as StandardIssueType[])} />
          </div>
        )}

        {errorType && (
          <div className={styles.followup}>
            <h3>{errorType === "OTHER" ? "请说明具体问题" : "补充说明（选填）"}</h3>
            <TextArea value={note} onChange={setNote} maxLength={500} rows={3} showCount placeholder="一句话说明现场情况" />
          </div>
        )}

        <div className={styles.popupActions}>
          <Button onClick={closeRejectPopup}>取消</Button>
          <Button color="primary" disabled={!canSubmitDisagreement} loading={submitting} onClick={submitDisagreement}>保存反馈</Button>
        </div>
      </Popup>
    </main>
  );
};

export default QualityDetail;
